import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBox, faTrash, faMinus, faPlus, faShoppingCart } from '@fortawesome/free-solid-svg-icons';
import '../styles/Cart.css';

function ShoppingCart({ cart, setCart }) {
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [error, setError] = useState('');

  const safeUpdateCart = (updater) => {
    setCart(prevCart => {
      const newCart = typeof updater === 'function' ? updater(prevCart) : updater;
      try { sessionStorage.setItem('cart', JSON.stringify(newCart)); } catch {}
      return newCart;
    });
  };

  const handleRemove = (idLevels) => {
    safeUpdateCart(prev => prev.filter(item => item.idLevels !== idLevels));
  };

  const handleQuantityChange = (idLevels, newValue) => {
    const item = cart.find(i => i.idLevels === idLevels);
    if (!item) return;
    const availQty = Number(item.availQty) || 0;
    const qty = parseInt(newValue) || 0;

    if (qty <= 0) { handleRemove(idLevels); return; }
    if (qty > availQty) {
      setError(`Max available for ${item.itemName} is ${availQty}`);
      setTimeout(() => setError(''), 3500);
      safeUpdateCart(prev => prev.map(ci =>
        ci.idLevels === idLevels ? { ...ci, quantity: availQty } : ci
      ));
      return;
    }
    safeUpdateCart(prev => prev.map(ci =>
      ci.idLevels === idLevels ? { ...ci, quantity: qty } : ci
    ));
  };

  const handleStep = (idLevels, delta) => {
    const item = cart.find(i => i.idLevels === idLevels);
    if (!item) return;
    handleQuantityChange(idLevels, item.quantity + delta);
  };

  const handleClearCart = () => {
    safeUpdateCart([]);
    setShowClearConfirm(false);
  };

  const totals = cart.reduce((acc, item) => {
    const ppu = Number(item.ppu) || 1;
    const qty = Number(item.quantity) || 0;
    return { units: acc.units + qty, pieces: acc.pieces + qty * ppu };
  }, { units: 0, pieces: 0 });

  if (cart.length === 0) {
    return (
      <div className="cart-empty">
        <div className="cart-empty-icon"><FontAwesomeIcon icon={faShoppingCart} /></div>
        <div className="cart-empty-text">Your cart is empty</div>
      </div>
    );
  }

  return (
    <div>
      {error && <div className="cart-error">{error}</div>}

      {/* Items */}
      <div>
        {cart.map(item => {
          const availQty = Number(item.availQty) || 0;
          const ppu = Number(item.ppu) || 1;
          return (
            <div key={item.idLevels} className="cart-item-row">
              <div className="cart-item-icon">
                <FontAwesomeIcon icon={faBox} />
              </div>

              <div className="cart-item-info">
                <div className="cart-item-name">{item.itemName}</div>
                <div className="cart-item-meta">
                  Lot {item.idLevels} · {item.itemNumber}
                  {item.notes ? ` · ${item.notes}` : ''}
                  {' · '}{(item.quantity * ppu).toLocaleString()} pcs · Avail: {availQty}
                </div>
              </div>

              {/* Qty stepper */}
              <div className="qty-stepper">
                <button
                  className="qty-btn"
                  onClick={() => handleStep(item.idLevels, -1)}
                  disabled={item.quantity <= 1}
                >
                  <FontAwesomeIcon icon={faMinus} style={{ fontSize: '0.65rem' }} />
                </button>
                <input
                  type="number"
                  className="qty-input"
                  value={item.quantity}
                  min={1}
                  max={availQty}
                  onChange={(e) => handleQuantityChange(item.idLevels, e.target.value)}
                  onBlur={(e) => handleQuantityChange(item.idLevels, e.target.value)}
                />
                <button
                  className="qty-btn"
                  onClick={() => handleStep(item.idLevels, 1)}
                  disabled={item.quantity >= availQty}
                >
                  <FontAwesomeIcon icon={faPlus} style={{ fontSize: '0.65rem' }} />
                </button>
              </div>

              <button className="cart-item-remove" onClick={() => handleRemove(item.idLevels)} title="Remove">
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          );
        })}
      </div>

      {/* Totals */}
      <div className="cart-totals">
        <div className="cart-total-item">
          <div className="cart-total-value">{totals.units.toLocaleString()}</div>
          <div className="cart-total-label">Total Units</div>
        </div>
        <div className="cart-total-item">
          <div className="cart-total-value">{totals.pieces.toLocaleString()}</div>
          <div className="cart-total-label">Total Pieces</div>
        </div>
        <div className="cart-total-item">
          <div className="cart-total-value">{cart.length}</div>
          <div className="cart-total-label">Line Items</div>
        </div>
      </div>

      {/* Clear confirm */}
      {showClearConfirm ? (
        <div style={{ marginTop: 14, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <span style={{ fontSize: '0.82rem', color: '#dc2626', fontWeight: 600 }}>Remove all items?</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="detail-btn detail-btn--ghost" style={{ padding: '5px 12px', fontSize: '0.78rem' }} onClick={() => setShowClearConfirm(false)}>Cancel</button>
            <button className="detail-btn" style={{ padding: '5px 12px', fontSize: '0.78rem', background: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 7 }} onClick={handleClearCart}>Clear Cart</button>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            className="detail-btn"
            style={{ padding: '5px 12px', fontSize: '0.75rem', color: '#94a3b8', border: '1px solid #e2e8f0', borderRadius: 7, background: '#fff' }}
            onClick={() => setShowClearConfirm(true)}
          >
            <FontAwesomeIcon icon={faTrash} style={{ marginRight: 5 }} />
            Clear All
          </button>
        </div>
      )}
    </div>
  );
}

export default ShoppingCart;
